import React, { memo } from 'react'
import { DatePicker as DatePickerDesktop } from 'components/forms/DatePicker'
import moment, { Moment } from 'moment-timezone'
import { Button } from 'transfix-ui/components/Button'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Box, makeStyles } from 'transfix-ui/core'
import { MaterialUiPickersDate } from 'transfix-ui/core/pickers'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { FleetActions } from 'apps/fleet-planner/components/fleet/FleetActions'
import { FLEET_RANGE } from 'apps/fleet-planner/types'
import { TopNavContainer } from 'components/layout'
import { CalendarIcon, DownArrowIcon, LeftArrowIcon, RightArrowIcon } from 'transfix-icons'
import { CalendarRangeSize } from './CalendarRangeSize'
import { DatePickerMobile } from './DatePickerMobile'

interface IFleetTopNavProps {
  rangeSize: FLEET_RANGE
  date: Moment
  onRangeSizeChange(rangeSize: FLEET_RANGE): void
  onDateChange(date: Moment): void
  className: string
}

const useStyles = makeStyles(() => ({
  datePickerLabel: {
    color: brandColors.black,
  },
}))

const getMonthLabel = (start: Moment, end: Moment): string => {
  if (start.isSame(end, 'y')) {
    return start.isSame(end, 'M')
      ? start.format('MMM YYYY')
      : `${start.format('MMM')} – ${end.format('MMM YYYY')}`
  }
  return `${start.format('MMM YYYY')} – ${end.format('MMM YYYY')}`
}

export const FleetTopNav = memo<IFleetTopNavProps>(
  ({ className, date, onDateChange, onRangeSizeChange, rangeSize }) => {
    const classes = useStyles()
    const { isMobile } = useBreakpointContext()

    const start = date.clone().subtract(rangeSize === FLEET_RANGE.WEEK ? date.day() : 0, 'd')
    const end = date
      .clone()
      .add((rangeSize === FLEET_RANGE.WEEK ? FLEET_RANGE.WEEK - date.day() : rangeSize) - 1, 'd')

    const todayHandler = () => onDateChange(moment())
    const prevHandler = () => onDateChange(date.clone().subtract(rangeSize, 'd'))
    const nextHandler = () => onDateChange(date.clone().add(rangeSize, 'd'))
    const dateChangeDesktopHandler = (dates: string[]) => {
      if (dates[0]) {
        const hasChanged =
          rangeSize === FLEET_RANGE.WEEK
            ? start.isAfter(dates[0]) || end.isBefore(dates[0])
            : !date.isSame(dates[0], 'd')
        if (hasChanged) onDateChange(moment(dates[0]))
      }
    }
    const dateChangeHandler = (nextDate: MaterialUiPickersDate) => {
      if (nextDate) {
        const hasChanged =
          rangeSize === FLEET_RANGE.WEEK
            ? start.isAfter(nextDate) || end.isBefore(nextDate)
            : !date.isSame(nextDate, 'd')
        if (hasChanged) onDateChange(moment(nextDate))
      }
    }

    return (
      <TopNavContainer className={className}>
        <div className='flex items-center'>
          {isMobile ? (
            // NOTE: This DatePicker was created specifically for the mobile version of the fleet view
            <DatePickerMobile value={date} onChange={dateChangeHandler} />
          ) : (
            // NOTE: This DatePicker doesn't work on mobile
            <DatePickerDesktop
              date={date.valueOf()}
              onDateChange={dateChangeDesktopHandler}
              readonly
              id='fleet-view-date-picker'>
              <Button className='body-medium' color='tertiary' data-test='fleet-view-date-picker'>
                <span className={classes.datePickerLabel}>{getMonthLabel(start, end)}</span>
                <DownArrowIcon />
              </Button>
            </DatePickerDesktop>
          )}
        </div>
        <div className='flex items-center'>
          {isMobile ? (
            <Box px={0.5} color={brandColors.skyBlue6}>
              <IconButton
                Icon={CalendarIcon}
                onClick={todayHandler}
                color='tertiary'
                label='Today'
                size='large'
              />
            </Box>
          ) : (
            <Button onClick={todayHandler} className='calendar-control mr4'>
              Today
            </Button>
          )}
          <Box px={0.5} color={brandColors.skyBlue6}>
            <IconButton
              Icon={LeftArrowIcon}
              onClick={prevHandler}
              color='tertiary'
              label='Previous period'
              size='large'
            />
          </Box>
          <Box px={0.5} color={brandColors.skyBlue6}>
            <IconButton
              Icon={RightArrowIcon}
              onClick={nextHandler}
              color='tertiary'
              label='Following period'
              size='large'
            />
          </Box>
          {!isMobile && (
            <div className='ml2'>
              <CalendarRangeSize rangeSize={rangeSize} onRangeSizeChange={onRangeSizeChange} />
            </div>
          )}
          {!isMobile && (
            <div className='calendar control ml6'>
              <FleetActions />
            </div>
          )}
        </div>
      </TopNavContainer>
    )
  }
)
